import React, { useEffect, useRef } from "react";
import { gsap } from "../../lib/gsap";
import { useSection } from "../../context/SectionContext";

/**
 * Fixed instrument readouts on the right edge: scroll position and the rate
 * meter. Both are written straight to the DOM inside GSAP's ticker, so
 * per-frame values never trigger a React render.
 */
const Hud = () => {
  const section = useSection();
  const progressRef = useRef(null);
  const barRef = useRef(null);
  const meterRef = useRef([]);

  useEffect(() => {
    const progress = progressRef.current;
    let lastProgress = "";
    let lastY = window.scrollY;
    let velocity = 0;
    let lastLevel = -1;

    const paint = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(Math.max(window.scrollY / max, 0), 1) : 0;

      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${ratio.toFixed(4)})`;
      }

      // Rate meter: real instrumentation — it reads the reader's own scroll
      // speed and decays back to rest. Bars repaint only on level change.
      const delta = Math.abs(window.scrollY - lastY);
      lastY = window.scrollY;
      const target = Math.min(delta / 48, 1);
      velocity += (target - velocity) * (target > velocity ? 0.55 : 0.07);

      const level = Math.round(velocity * meterRef.current.length);
      if (level !== lastLevel) {
        lastLevel = level;
        meterRef.current.forEach((bar, i) => {
          if (!bar) return;
          bar.style.background = i < level ? "var(--flare)" : "var(--line-strong)";
        });
      }

      if (progress) {
        const pct = String(Math.round(ratio * 100)).padStart(3, "0");
        if (pct !== lastProgress) {
          lastProgress = pct;
          progress.textContent = pct;
        }
      }
    };

    paint();
    gsap.ticker.add(paint);
    return () => gsap.ticker.remove(paint);
  }, []);

  return (
    <>
      {/* Scroll progress — one hairline under the bar */}
      <div
        ref={barRef}
        className="pointer-events-none fixed inset-x-0 top-14 z-hud h-px origin-left"
        style={{ transform: "scaleX(0)", background: "var(--fg)" }}
        aria-hidden="true"
      />

      {/* Right edge only, and out of the way in the closing chapter */}
      <div
        className={`pointer-events-none fixed bottom-0 right-0 z-hud hidden items-end gap-3 pb-5 pr-margin transition-opacity duration-500 md:flex ${
          section?.activeId === "contact" ? "opacity-0" : "opacity-100"
        }`}
        aria-hidden="true"
      >
        <span className="t-label">
          Scroll <span ref={progressRef} className="t-num text-fg">000</span>
        </span>

        <span className="flex items-end gap-[3px]" title="Scroll rate">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              ref={(el) => {
                meterRef.current[i] = el;
              }}
              className="block w-[3px]"
              style={{ height: 5 + i * 3, background: "var(--line-strong)" }}
            />
          ))}
        </span>
      </div>
    </>
  );
};

export default Hud;
